import { Pipe, PipeTransform } from '@angular/core';

/**
 * Zeigt Wochentage in einer kompakten, lesbaren Form an:
 * - Kein Wochentag: leerer String
 * - Ein Wochentag: "Dienstag"
 * - Mehrere, einzelne Wochentage: "Mo, Mi, Sa"
 * - Mehrere, zusammenhängende Wochentage: "Mo-Di, Do-So"
 * - Einzelne und zusammenhängende Wochentage: "Mo-Di, Do, Fr-Sa"
 * - Alle Wochentage: "alle"
 */
@Pipe({
  name: 'weekday',
  standalone: true
})
export class WeekdayPipe implements PipeTransform {

private readonly WEEKDAYS_LONG = [
    'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'
  ];

  private readonly WEEKDAYS_SHORT = [
    'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'
  ];

  /**
   * Transformiert eine Liste von Wochentagen in eine kompakte Darstellung
   *
   * @param days Eine Liste mit Wochentagnamen oder Indizes (0-6 oder 1-7)
   * @param options Konfigurationsoptionen
   * @returns Formatierter String
   */
  transform(
    days: (string)[] | null,
    options: { useShortNames?: boolean, allText?: string } = {}
  ): string {
    // Default-Werte für die Optionen
    const { useShortNames = true, allText = 'alle' } = options;

    // Handle leere und null-Werte
    if (!days || days.length === 0) {
      return '';
    }

    // Normalisiere Eingabe zu Indizes (0-6)
    const normalizedDays = this.normalizeDays(days);

    // Wenn alle 7 Tage ausgewählt sind
    if (normalizedDays.length === 7) {
      return allText;
    }

    // Einzelner Tag? Dann gib den vollständigen Namen zurück
    if (normalizedDays.length === 1) {
      return this.WEEKDAYS_LONG[normalizedDays[0]];
    }

    // Gruppiere aufeinanderfolgende Tage
    const groups = this.groupConsecutiveDays(normalizedDays);

    // Formatiere jede Gruppe
    const formattedGroups = groups.map(group => {
      if (group.length === 1) {
        // Einzelner Tag
        return useShortNames ? this.WEEKDAYS_SHORT[group[0]] : this.WEEKDAYS_LONG[group[0]];
      } else {
        // Aufeinanderfolgende Tage
        const weekdayNames = useShortNames ? this.WEEKDAYS_SHORT : this.WEEKDAYS_LONG;
        return `${weekdayNames[group[0]]}-${weekdayNames[group[group.length - 1]]}`;
      }
    });

    return formattedGroups.join(', ');
  }

  /**
   * Normalisiert Eingabewerte zu Indizes von 0-6
   */
  private normalizeDays(days: (string | number)[]): number[] {
    return days
      .map(day => {
        if (typeof day === 'number') {
          // Wenn Index zwischen 1-7, konvertiere zu 0-6
          return day >= 1 && day <= 7 ? (day - 1) % 7 : day;
        } else if (typeof day === 'string') {
          // Suche Index des Wochentags
          const index = this.WEEKDAYS_LONG.findIndex(
            d => d.toLowerCase() === day.toLowerCase()
          );
          return index;
        }
        return -1;
      })
      .filter(day => day >= 0 && day <= 6) // Nur gültige Tage behalten
      .sort((a, b) => a - b); // Nach Wochentag sortieren
  }

  /**
   * Gruppiert aufeinanderfolgende Tage
   */
  private groupConsecutiveDays(days: number[]): number[][] {
    if (days.length === 0) return [];

    const result: number[][] = [];
    let currentGroup: number[] = [days[0]];

    for (let i = 1; i < days.length; i++) {
      // Wenn der aktuelle Tag direkt auf den vorherigen folgt
      if (days[i] === days[i - 1] + 1) {
        currentGroup.push(days[i]);
      } else {
        // Andernfalls starte eine neue Gruppe
        result.push([...currentGroup]);
        currentGroup = [days[i]];
      }
    }

    result.push(currentGroup);
    return result;
  }

}
